import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert, CircularProgress, Box } from "@mui/material";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
});

const getLocalDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox"
        ? false
        : f.type === "number"
        ? ""
        : f.type === "date"
        ? getLocalDate()
        : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

export default function CreateBalance() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [loadingOpts, setLoadingOpts] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const previousDataRef = useRef({ customers: [], items: [] });
  const [lastRefreshed, setLastRefreshed] = useState(Date.now());

  const loadOptions = useCallback(async () => {
    setLoadingOpts(true);
    try {
      const t = Date.now();
      const [cRes, iRes] = await Promise.all([
        api.get("/api/customer", { params: { t } }),
        api.get("/api/item", { params: { t } }),
      ]);
      const arr = (x) => (Array.isArray(x) ? x : []);
      const newCustomers = arr(cRes?.data)
        .filter((c) =>
          ["supplier", "suppliers"].includes(
            String(c?.type ?? "").toLowerCase()
          )
        )
        .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
      const newItems = arr(iRes?.data).sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "")
      );
      const changed =
        JSON.stringify(previousDataRef.current.customers) !==
          JSON.stringify(newCustomers) ||
        JSON.stringify(previousDataRef.current.items) !==
          JSON.stringify(newItems);
      setCustomers(newCustomers);
      setItems(newItems);
      previousDataRef.current = { customers: newCustomers, items: newItems };
      if (changed) setLastRefreshed(Date.now());
    } catch (e) {
      setToast({ open: true, msg: "Failed to load dropdowns", sev: "error" });
      setCustomers([]);
      setItems([]);
    } finally {
      setLoadingOpts(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);
  useEffect(() => {
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOptions]);

  const FIELDS = useMemo(() => {
    const hasData = !loadingOpts && customers.length + items.length > 0;
    return [
      {
        name: "agentName",
        label: "Agent Name",
        type: "text",
        required: false,
        span: 2,
        default: "Seblu Deresse Mekonnen",
      },
      {
        name: "customerId",
        label: hasData ? "Customer" : "Loading customers...",
        type: "select",
        required: true,
        span: 2,
        options: customers.map((c) => ({ label: c.name, value: String(c.id) })),
        props: { disabled: loadingOpts || customers.length === 0 },
      },
      {
        name: "itemId",
        label: hasData ? "Item" : "Loading items...",
        type: "select",
        required: false,
        span: 2,
        options: items.map((i) => ({ label: i.name, value: String(i.id) })),
        props: { disabled: loadingOpts || items.length === 0 },
      },
      {
        name: "date",
        label: "Date",
        type: "date",
        required: true,
        span: 2,
        default: getLocalDate(),
      },
      {
        name: "invoiceAmount",
        label: "Invoice Amount",
        type: "number",
        required: false,
        span: 2,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }

      },
      {
        name: "bankDeposit",
        label: "Bank Deposit",
        type: "number",
        required: false,
        span: 2,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
},

      {
        name: "withHold",
        label: "WithHold",
        type: "number",
        required: false,
        span: 2,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "adjustment",
        label: "Adjustment",
        type: "number",
        required: false,
        span: 2,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "minusedFromDept",
        label: "Minused From Dept",
        type: "number",
        required: false,
        span: 2,
        min: 0,
        props: {
    placeholder: "Enter amount",
    inputProps: { step: 0.01, min: 0 },
    onKeyDown: (e) => {
      if (e.key === "-" || e.key === "e") {
        e.preventDefault();
      }
    }
  }
      },
      {
        name: "minusedReason",
        label: "Minused Reason",
        type: "text",
        required: false,
        span: 2,
      },
    ];
  }, [loadingOpts, customers, items]);

  const SCHEMA = useMemo(() => buildSchema(FIELDS), [FIELDS]);
  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const { data, error } = await postJSON("/api/balance-sheet", values);
    setSubmitting(false);
    if (error) {
      setToast({ open: true, msg: String(error), sev: "error" });
      return;
    }
    setToast({ open: true, msg: "Balance Sheet created!", sev: "success" });
    resetForm();
  };

  const LoadingBar = loadingOpts ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <CircularProgress size={18} />
      <span>Loading options...</span>
    </Box>
  ) : null;

  return (
    <>
      <SimpleForm
        key={lastRefreshed}
        title="CREATE BALANCE SHEET"
        subtitle="Add a new balance sheet entry"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Balance Sheet"
        columns={4}
      />
      {LoadingBar}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert severity={toast.sev} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
